<template>
  <div id="auth-view" :class="[getAuthClass, { 'is-landing': isLandingPage }]" :style="getAuthBg">
    <div class="auth-landing-hero" :class="{ 'is-landing': isLandingPage }">
      <div ref="authModalRef" class="auth-modal" :class="{ 'is-mobile': isMobile, 'image-expanded': imageExpanded }">
        <div v-if="connecting" class="connecting">
          <simple-content-dialog v-bind="getConnecting" />
        </div>

        <router-view v-slot="{ Component }">
          <transition name="router" mode="out-in">
            <component v-if="isAuthLoaded" :is="Component" class="auth-wrapper" />
          </transition>
        </router-view>

        <div v-if="!connecting && isBackButtonVisible" class="back-button">
          <gam-button v-bind="getBackButton" />
        </div>

        <div v-if="!connecting && isContinueButtonVisible" class="continue-button">
          <gam-button v-bind="getContinueButton" />
        </div>

        <div v-if="!connecting && isLogoutButtonVisible" class="logout-button">
          <gam-button v-bind="getLogoutButton" />
        </div>
      </div>
    </div>

    <div v-if="isLandingPage" class="auth-social">
      <!-- Vienna as the map shows it, with two of its spots pinned the way the app pins them. -->
      <div
        ref="cityRef"
        class="auth-city"
        :class="{ 'is-pin-shown': isCityPinShown }"
        :style="{ '--city-image': `url(${viennaMap})` }"
      >
        <div class="auth-city-map" aria-hidden="true" />
        <div class="auth-city-pins zoom-default">
          <button
            type="button"
            class="city-spot is-top location-circle lc-active lc-checkin lc-irl lc-live"
            :aria-label="localize('auth.landing.start')"
            @click="scrollToAuth"
          >
            <span class="lc-ring">
              <span class="lc-main"><img :src="spotDonaupark" alt="" /></span>
              <span class="lc-badge lc-badge-tr"><span class="lc-badge-icon pawn" /></span>
              <span class="lc-badge lc-badge-br"><span class="lc-badge-icon play" /></span>
            </span>
            <span class="lc-name">
              <span>{{ localize('auth.landing.city.spot.donaupark') }}</span>
            </span>
            <span class="lc-temporal">
              <span>{{ localize('app.component.map.pin.live') }}</span>
            </span>
          </button>
          <button
            type="button"
            class="city-spot is-bottom location-circle lc-active lc-board"
            :aria-label="localize('auth.landing.start')"
            @click="scrollToAuth"
          >
            <span class="lc-floating-board"><span class="lc-board-icon" /></span>
            <span class="lc-ring">
              <span class="lc-main"><img :src="spotAdeleJellinekPark" alt="" /></span>
            </span>
            <span class="lc-name">
              <span>{{ localize('auth.landing.city.spot.adele') }}</span>
            </span>
          </button>
        </div>
        <div class="showcase-sport-hero">
          <button
            type="button"
            class="showcase-crown"
            :aria-label="localize('master.dialog.step.play.story')"
            @click="scrollToBook"
          >
            <HandshakeMetric class="showcase-sphere" :icon="GamIconName.CROWN" aria-hidden="true" />
          </button>
          <div class="showcase-title-stack">
            <p class="showcase-sport-title">
              <span
                ><span class="is-accent">{{ localize('auth.landing.unplayed.accent') }}</span
                >{{ localize('auth.landing.unplayed.rest') }}</span
              >
            </p>
            <p class="showcase-step-line">
              {{ localize('auth.landing.unplayed.line') }}<br />
              {{ localize('auth.landing.unplayed.line.rest') }}
            </p>
          </div>
        </div>
      </div>
      <!-- One evening in the order it happens: a plan, a handshake, a game. -->
      <div class="showcase-step">
        <p class="showcase-part-title"><span class="is-accent">1</span>{{ localize('auth.landing.step.plan') }}</p>
        <MasterSpark :faces="FREESTYLE_AVATARS.slice(0, 3)" @play="scrollToAuth" />
      </div>
      <div class="showcase-step">
        <p class="showcase-part-title"><span class="is-accent">2</span>{{ localize('auth.landing.step.shake') }}</p>
        <MasterOffer :face-src="FREESTYLE_AVATARS[3]" @play="scrollToAuth" />
        <div class="showcase-step-between">
          <p class="showcase-step-or">{{ localize('auth.landing.step.shake.tap') }}</p>
          <p class="showcase-step-or">{{ localize('auth.landing.step.shake.or') }}</p>
        </div>
        <MasterHandshake :face-src="FREESTYLE_AVATARS[3]" />
      </div>
      <div class="showcase-step">
        <p class="showcase-part-title"><span class="is-accent">3</span>{{ localize('auth.landing.step.play') }}</p>
        <button
          type="button"
          class="shuffle-pair-button"
          :aria-label="localize('auth.landing.start')"
          @click="scrollToAuth"
        >
          <MasterPair
            :position-index="freestyleIndex"
            :face-src="freestyleAvatarSrc"
            :player-name="freestylePlayerName"
            :visitor-white="freestyleVisitorWhite"
          />
        </button>
      </div>
    </div>

    <!-- What the story leaves each player with: their own pairs, one position with one person. -->
    <div v-if="isLandingPage" class="auth-collection">
      <div class="showcase-sport-hero">
        <div class="showcase-title-stack">
          <p class="showcase-sport-title">
            <span>
              {{ localize('auth.landing.collection.title.lead') }}
              <span class="is-accent">{{ localize('auth.landing.collection.title.count') }}</span>
              {{ localize('auth.landing.collection.title.rest') }}
            </span>
          </p>
          <p class="showcase-step-line">{{ localize('auth.landing.collection.line') }}</p>
        </div>
      </div>
      <MasterCollection :pairs="COLLECTION_PAIRS" :partner-name="localize('auth.landing.offer.name')" />
    </div>

    <!-- The story, already open on its first page: swiping turns it, no tap to begin. -->
    <div v-if="isLandingPage" ref="bookRef" class="auth-book">
      <div class="showcase-sport-hero">
        <p class="showcase-sport-title is-multiline">
          <span>{{ localize('auth.landing.nature') }}</span>
          <span class="showcase-title-line">
            <span>{{ localize('master.story.road') }}</span>
            <span class="is-accent">{{ localize('auth.landing.master.times') }}</span>
            <span>{{ localize('master.story.road') }}</span>
          </span>
        </p>
      </div>
      <MastersBook inline />
    </div>

    <!-- The walk and the tree share one park; at the end of it, the spot is pinned on the photo's own tree. -->
    <div v-if="isLandingPage" class="auth-park" :style="{ '--park-image': `url(${parkPhoto})` }">
      <div class="auth-park-photo">
        <button
          type="button"
          class="tree-spot"
          :class="{ 'is-sphere': isTreeInView, 'is-pin': isTreePinShown }"
          :aria-label="`${localize('auth.landing.move')} ${localize('auth.landing.unseen.accent')}${localize('auth.landing.unseen.rest')}`"
          @click="onTreeSpotClick"
        >
          <span class="tree-spot-mark">
            <span class="tree-spot-rim" aria-hidden="true" />
            <HandshakeMetric class="tree-spot-sphere" :icon="GamIconName.CROWN" />
            <span class="tree-spot-pin">
              <span class="tree-spot-board">
                <span class="tree-spot-board-icon" />
              </span>
              <span class="tree-spot-ring">
                <span class="tree-spot-main">
                  <img :src="treeSpot" alt="" />
                </span>
              </span>
            </span>
          </span>
        </button>
      </div>
      <div class="auth-showcase">
        <div class="showcase-sport-hero">
          <div class="showcase-title-stack">
            <p class="showcase-sport-title">{{ localize('auth.landing.off') }}</p>
            <p class="showcase-part-title">
              <span>{{ localize('auth.landing.title.solve') }}</span>
              <span class="is-accent">{{ fieldLabel }}</span>
              <span>{{ localize('auth.landing.title.walk') }}</span>
            </p>
          </div>
        </div>
        <div class="showcase-board-stage">
          <div class="walk-path-bg">
            <svg class="walk-path-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
              <polyline
                class="walk-streak-line"
                points="38,72 44,65 48,56 42,50 36,44 32,38 38,34 46,30"
                fill="none"
                stroke="white"
                stroke-width="2.5"
                vector-effect="non-scaling-stroke"
              />
            </svg>
            <!-- start spot -->
            <div class="walk-spot" style="left: 6%; top: 95%"><span class="walk-spot-empty" /></div>
            <!-- initial ascent -->
            <span class="walk-sq walk-sq-correct" style="left: 12%; top: 90%" />
            <span class="walk-sq walk-sq-correct" style="left: 18%; top: 84%" />
            <span class="walk-sq walk-sq-incorrect" style="left: 24%; top: 78%" />
            <span class="walk-sq walk-sq-correct" style="left: 30%; top: 75%" />
            <span class="walk-sq walk-sq-corrected" style="left: 34%; top: 72%">
              <span class="walk-corrected-dot" />
            </span>
            <!-- streak: curve left then up -->
            <span class="walk-sq walk-sq-correct" style="left: 38%; top: 72%" />
            <span class="walk-sq walk-sq-correct" style="left: 44%; top: 65%" />
            <span class="walk-sq walk-sq-correct" style="left: 48%; top: 56%" />
            <span class="walk-sq walk-sq-correct" style="left: 42%; top: 50%" />
            <span class="walk-sq walk-sq-correct" style="left: 36%; top: 44%" />
            <span class="walk-sq walk-sq-correct" style="left: 32%; top: 38%" />
            <span class="walk-sq walk-sq-correct" style="left: 38%; top: 34%" />
            <span class="walk-sq walk-sq-correct" style="left: 46%; top: 30%" />
            <!-- corrected + final stretch -->
            <span class="walk-sq walk-sq-corrected" style="left: 54%; top: 28%">
              <span class="walk-corrected-dot" />
            </span>
            <span class="walk-sq walk-sq-correct" style="left: 62%; top: 26%" />
            <span class="walk-sq walk-sq-incorrect" style="left: 70%; top: 29%" />
            <span class="walk-sq walk-sq-correct" style="left: 76%; top: 25%" />
            <span class="walk-sq walk-sq-correct" style="left: 84%; top: 23%" />
            <!-- reveal spot -->
            <div class="walk-spot" style="left: 92%; top: 21%"><span class="walk-spot-full" /></div>
          </div>

          <div class="showcase-section">
            <div class="board-group">
              <div class="board-area">
                <div class="board-inner">
                  <div class="walkfield-grid" :style="{ '--zoom-n': boardZoomLevel }">
                    <template v-if="boardZoomLevel === 8">
                      <template v-for="row in 8" :key="row">
                        <div
                          v-for="col in 8"
                          :key="`${row}-${col}`"
                          class="walkfield-cell"
                          :class="{
                            selected: selectedSpeed === 9 - row && selectedBand === BAND_LETTERS[col - 1],
                          }"
                          @click="selectField(9 - row, BAND_LETTERS[col - 1])"
                        >
                          <span
                            v-if="selectedSpeed === 9 - row && selectedBand === BAND_LETTERS[col - 1]"
                            class="walkfield-selected-label"
                          >
                            {{ BAND_LETTERS[col - 1].toUpperCase() }}{{ 9 - row }}
                          </span>
                          <template v-else>
                            <span v-if="col === 1" class="walkfield-row-label">{{ 9 - row }}</span>
                            <span v-if="row === 8" class="walkfield-col-label">{{ BAND_LETTERS[col - 1] }}</span>
                          </template>
                        </div>
                      </template>
                    </template>
                    <template v-else>
                      <div
                        v-for="field in visibleZoomFields"
                        :key="`${field.speed}${field.band}`"
                        class="walkfield-cell"
                        :class="{ selected: selectedSpeed === field.speed && selectedBand === field.band }"
                        @click="selectField(field.speed, field.band)"
                      >
                        <span class="walkfield-zoom-label"> {{ field.band.toUpperCase() }}{{ field.speed }} </span>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div class="board-status-row">
                <span>
                  Walk <span class="status-field">{{ fieldLabel }}</span> · {{ ratingRange }} · {{ selectedSpeed }}km/h
                </span>
              </div>
            </div>

            <div class="start-cta-row">
              <button
                type="button"
                class="zoom-glyph"
                :class="{ disabled: boardZoomLevel >= 8 }"
                :disabled="boardZoomLevel >= 8"
                aria-label="Zoom out"
                @click="zoomBoardOut"
              >
                <span class="zoom-glyph-icon" aria-hidden="true">−</span>
              </button>
              <button class="start-cta" @click="scrollToAuth">{{ localize('auth.landing.start') }}</button>
              <button
                type="button"
                class="zoom-glyph"
                :class="{ disabled: boardZoomLevel <= 1 }"
                :disabled="boardZoomLevel <= 1"
                aria-label="Zoom in"
                @click="zoomBoardIn"
              >
                <span class="zoom-glyph-icon" aria-hidden="true">+</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div ref="treeRef" class="auth-tree">
        <div class="showcase-sport-hero">
          <p class="showcase-part-title">
            <span>{{ localize('auth.landing.move') }}</span>
            <span
              ><span class="is-accent">{{ localize('auth.landing.unseen.accent') }}</span
              >{{ localize('auth.landing.unseen.rest') }}</span
            >
          </p>
        </div>
      </div>
    </div>

    <!-- Nothing to download: the app runs in the browser, and pins to the home screen from here. -->
    <footer v-if="isLandingPage && !isPWA" class="auth-footer">
      <p class="auth-footer-risk">
        {{ localize('auth.landing.footer.risk') }}
        <span class="is-accent">{{ localize('auth.landing.footer.risk.accent') }}</span
        ><br />
        {{ localize('auth.landing.footer.risk.rest') }}
        <span class="is-accent">{{ localize('auth.landing.footer.risk.game') }}</span>
        {{ localize('auth.landing.footer.risk.end') }}
      </p>
      <div class="auth-footer-pin">
        <span class="auth-footer-line">{{ localize('auth.landing.footer.browser') }}</span>
        <GamInstallPin />
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { LoginMethodType } from '@/core/data/auth/auth.type';
import { RouterAuthType, RouterNameType } from '@/core/data/config/uiConfig.interface';
import { gambits, localize } from '@/core/gambits';
import { isMobile, isPWA } from '@/core/helpers/ui.helper';
import { useAppStore } from '@/stores/app.store';
import { useAuthStore } from '@/stores/auth.store';
import SimpleContentDialog from '@/views/components/gam-dialog/dialog-contents/SimpleContentDialog.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamInstallPin from '@/views/components/GamInstallPin.vue';
import MasterCollection, { type MasterCollectionPair } from '@/views/components/MasterCollection.vue';
import MasterHandshake from '@/views/components/MasterHandshake.vue';
import MasterOffer from '@/views/components/MasterOffer.vue';
import MasterPair from '@/views/components/MasterPair.vue';
import MastersBook from '@/views/components/MastersBook.vue';
import MasterSpark from '@/views/components/MasterSpark.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { SimpleContentDialogType } from '@/views/composables/models/dialog.interface';
import HandshakeMetric from '@/views/main/handshakes/HandshakeMetric.vue';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, onBeforeUnmount, ref, watch, type StyleValue } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();

const authStore = useAuthStore();
const { connecting, user, userDetails, redirectAfterAuth } = storeToRefs(authStore);
const appStore = useAppStore();
const { imageExpanded, imageUploading } = storeToRefs(appStore);

const isAuthLoaded = ref<boolean>(false);
const isLandingPage = computed(() => route.name === RouterAuthType.AUTH_SELECT);
const FREESTYLE_COUNT = 960;
const FREESTYLE_AVATARS = [
  new URL('../../ui/images/auth/man_1.svg', import.meta.url).href,
  new URL('../../ui/images/auth/man_2.svg', import.meta.url).href,
  new URL('../../ui/images/auth/unisex_1.svg', import.meta.url).href,
  new URL('../../ui/images/auth/woman_1.svg', import.meta.url).href,
  new URL('../../ui/images/auth/woman_2.svg', import.meta.url).href,
];
const freestyleIndex = ref(Math.floor(Math.random() * FREESTYLE_COUNT));
const freestyleAvatar = ref(Math.floor(Math.random() * FREESTYLE_AVATARS.length));
const freestyleAvatarSrc = computed(() => FREESTYLE_AVATARS[freestyleAvatar.value]);
/** One handle per face, in the same order. Keira is the player the offer already names. */
const FREESTYLE_NAMES = [
  'auth.landing.pair.jonas',
  'auth.landing.pair.leo',
  'auth.landing.pair.noa',
  'auth.landing.offer.name',
  'auth.landing.pair.mira',
];
const freestylePlayerName = computed(() => localize(FREESTYLE_NAMES[freestyleAvatar.value]));
/** White or black, so the rank is not always the same colour. */
const freestyleVisitorWhite = ref(Math.random() < 0.5);
let freestyleTimer: ReturnType<typeof setInterval> | undefined;

function nextFreestyleIndex() {
  let next = Math.floor(Math.random() * FREESTYLE_COUNT);
  if (next === freestyleIndex.value) next = (next + 1) % FREESTYLE_COUNT;
  freestyleIndex.value = next;

  let avatar = Math.floor(Math.random() * FREESTYLE_AVATARS.length);
  if (avatar === freestyleAvatar.value) avatar = (avatar + 1) % FREESTYLE_AVATARS.length;
  freestyleAvatar.value = avatar;

  freestyleVisitorWhite.value = !freestyleVisitorWhite.value;
}

function stopFreestyleCycle() {
  if (!freestyleTimer) return;
  clearInterval(freestyleTimer);
  freestyleTimer = undefined;
}

function startFreestyleCycle() {
  stopFreestyleCycle();
  freestyleTimer = setInterval(nextFreestyleIndex, 1000);
}

watch(
  isLandingPage,
  (isLanding) => {
    if (isLanding) startFreestyleCycle();
    else stopFreestyleCycle();
  },
  { immediate: true },
);

onBeforeUnmount(stopFreestyleCycle);

/** The handshake's player first, then two more pairs on the way. */
const COLLECTION_PAIRS: MasterCollectionPair[] = [
  { index: 54, face: FREESTYLE_AVATARS[3] },
  { index: 312, face: FREESTYLE_AVATARS[0] },
  { index: 731, face: FREESTYLE_AVATARS[4] },
];

const authModalRef = ref<HTMLElement | null>(null);

const BAND_LETTERS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const BAND_RATINGS: Record<string, string> = {
  a: '< 600',
  b: '600–800',
  c: '800–1000',
  d: '1000–1300',
  e: '1300–1600',
  f: '1600–1900',
  g: '1900–2200',
  h: '2200+',
};
const selectedBand = ref('d');
const selectedSpeed = ref(5);
const fieldLabel = computed(() => `${selectedBand.value.toUpperCase()}${selectedSpeed.value}`);
const ratingRange = computed(() => BAND_RATINGS[selectedBand.value]);

function selectField(speed: number, band: string) {
  selectedBand.value = band;
  selectedSpeed.value = speed;
}

const ZOOM_LEVELS = [8, 4, 2, 1];
const boardZoomLevel = ref(8);
const zoomFields = ref<{ speed: number; band: string }[] | null>(null);

// The landing board has no walk history, so every field ties at zero progress
// and the ranked subset collapses to the tie-break shuffle.
function buildZoomFields(): { speed: number; band: string }[] {
  const fields: { speed: number; band: string }[] = [];
  for (let speed = 1; speed <= 8; speed++) {
    for (const band of BAND_LETTERS) {
      fields.push({ speed, band });
    }
  }
  for (let i = fields.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [fields[i], fields[j]] = [fields[j], fields[i]];
  }
  return fields;
}

const visibleZoomFields = computed(() => {
  if (boardZoomLevel.value === 8 || !zoomFields.value) return [];
  return zoomFields.value.slice(0, boardZoomLevel.value * boardZoomLevel.value);
});

function applyZoomLevel(level: number) {
  boardZoomLevel.value = level;
  if (level === 8) return;
  if (!zoomFields.value) {
    zoomFields.value = buildZoomFields();
  }
  const first = zoomFields.value[0];
  if (first) selectField(first.speed, first.band);
}

function zoomBoardIn() {
  const idx = ZOOM_LEVELS.indexOf(boardZoomLevel.value);
  if (idx < ZOOM_LEVELS.length - 1) applyZoomLevel(ZOOM_LEVELS[idx + 1]);
}

function zoomBoardOut() {
  const idx = ZOOM_LEVELS.indexOf(boardZoomLevel.value);
  if (idx > 0) applyZoomLevel(ZOOM_LEVELS[idx - 1]);
}

function scrollToAuth() {
  authModalRef.value?.scrollIntoView({ behavior: 'auto', block: 'center' });
}

const cityRef = ref<HTMLElement | null>(null);
const isCityPinShown = ref(false);
let cityObserver: IntersectionObserver | undefined;

/* The city's spots drop in once most of the map is on screen, like the tree's pin. */
watch(cityRef, (city) => {
  cityObserver?.disconnect();
  cityObserver = undefined;
  isCityPinShown.value = false;
  if (!city) return;

  cityObserver = new IntersectionObserver(([entry]) => (isCityPinShown.value = entry.isIntersecting), {
    threshold: 0.5,
  });
  cityObserver.observe(city);
});

const treeRef = ref<HTMLElement | null>(null);
const isTreeInView = ref(false);
const isNearPageBottom = ref(false);
/** A tap on the glass, so the pin shows before the bottom of the page. */
const pinOpened = ref(false);
/** A tap on the pin, so the glass stays even at the bottom of the page. */
const pinDismissed = ref(false);
let treeObserver: IntersectionObserver | undefined;
/** Where the glass appeared, so the pin only follows a long scroll past it. */
let sphereScrollTop: number | null = null;

/** How far past the glass, as a share of the screen, before the pin takes over. */
const PIN_AFTER = 0.9;

const isTreePinShown = computed(
  () => isTreeInView.value && !pinDismissed.value && (isNearPageBottom.value || pinOpened.value),
);

function scrollPlace(): { top: number; height: number } {
  const app = document.getElementById('gambits-app');
  const appTop = app?.scrollTop ?? 0;
  const windowTop = window.scrollY || document.documentElement.scrollTop;
  if (app && appTop >= windowTop) return { top: appTop, height: app.clientHeight };
  return { top: windowTop, height: window.innerHeight };
}

function updatePinFromScroll() {
  if (!isTreeInView.value || sphereScrollTop == null) return;
  const { top, height } = scrollPlace();
  const near = top - sphereScrollTop >= height * PIN_AFTER;
  if (!near) pinDismissed.value = false;
  isNearPageBottom.value = near;
}

function hideTreeSpot() {
  isTreeInView.value = false;
  isNearPageBottom.value = false;
  sphereScrollTop = null;
  pinOpened.value = false;
  pinDismissed.value = false;
}

/* The glass comes on as the tree arrives. The pin waits until the page has moved well past it. */
function onTreeScroll(entry: IntersectionObserverEntry) {
  if (!entry.isIntersecting) {
    hideTreeSpot();
    return;
  }
  if (isTreeInView.value) return;
  isTreeInView.value = true;
  sphereScrollTop = scrollPlace().top;
  isNearPageBottom.value = false;
}

/** The glass and the pin trade places. This spot does not leave the page. */
function onTreeSpotClick() {
  if (isTreePinShown.value) {
    pinOpened.value = false;
    pinDismissed.value = true;
    return;
  }
  pinDismissed.value = false;
  pinOpened.value = true;
}

watch(treeRef, (tree) => {
  treeObserver?.disconnect();
  treeObserver = undefined;
  hideTreeSpot();
  if (!tree) return;

  treeObserver = new IntersectionObserver(([entry]) => onTreeScroll(entry), { threshold: 0 });
  treeObserver.observe(tree);
});

document.getElementById('gambits-app')?.addEventListener('scroll', updatePinFromScroll, { passive: true });
window.addEventListener('scroll', updatePinFromScroll, { passive: true });

onBeforeUnmount(() => {
  cityObserver?.disconnect();
  treeObserver?.disconnect();
  document.getElementById('gambits-app')?.removeEventListener('scroll', updatePinFromScroll);
  window.removeEventListener('scroll', updatePinFromScroll);
});

const bookRef = ref<HTMLElement | null>(null);

function scrollToBook() {
  bookRef.value?.scrollIntoView({ behavior: 'auto', block: 'center' });
}

const isBackButtonVisible = computed(() =>
  [RouterAuthType.AUTH_EMAIL, RouterAuthType.AUTH_OTP].includes(route.name as RouterAuthType),
);

const isLogoutButtonVisible = computed(() =>
  [RouterAuthType.AUTH_REGISTRATION, RouterAuthType.AUTH_RATINGS].includes(route.name as RouterAuthType),
);

const isContinueButtonVisible = computed(() => route.name === RouterAuthType.AUTH_PROFILE_IMAGES);

const getAuthClass = computed(() => {
  if (window.isVisualTest) {
    return 'no-rotate';
  }

  return 'hue-rotate';
});

const getBackButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.SMALL_ARROW_LEFT,
    isIconOnly: true,
    size: GamButtonSize.ACTIONS,
    variant: GamButtonVariant.PRIMARY,
    onClick: () => {
      if (route.name === RouterAuthType.AUTH_EMAIL) {
        router.push({ name: RouterAuthType.AUTH_SELECT });
      } else if (route.name === RouterAuthType.AUTH_OTP) {
        router.push({ name: RouterAuthType.AUTH_EMAIL });
      }
    },
  };
});

const getLogoutButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.X_CLOSE,
    isIconOnly: true,
    size: GamButtonSize.ACTIONS,
    variant: GamButtonVariant.PRIMARY,
    onClick: async () => {
      if (window.confirm('Are you sure you want to log out?')) {
        await gambits.authService.logout();
        router.push({ name: RouterNameType.LOCATIONS });
      }
    },
  };
});

const getContinueButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.SMALL_ARROW_RIGHT,
    isIconOnly: true,
    size: GamButtonSize.ACTIONS,
    variant: GamButtonVariant.PRIMARY,
    isLoading: imageUploading.value,
    isDisabled: imageUploading.value,
    onClick: () => {
      finishOnboarding();
    },
  };
});

function finishOnboarding() {
  router.push(redirectAfterAuth.value || { name: RouterNameType.SPARKS });
  authStore.clearRedirectPath();
}

const treeSpot = new URL('../../ui/images/auth/tree-spot.jpg', import.meta.url).href;
const parkPhoto = new URL('../../ui/images/auth/park.jpg', import.meta.url).href;
const viennaMap = new URL('../../ui/images/auth/vienna-map.webp', import.meta.url).href;
const spotDonaupark = new URL('../../ui/images/auth/spot-donaupark.jpg', import.meta.url).href;
const spotAdeleJellinekPark = new URL('../../ui/images/auth/spot-adele-jellinek-park.jpg', import.meta.url).href;
const otpImg = new URL('../../ui/images/auth/otp-view.jpg', import.meta.url).href;
const registerImg = new URL('../../ui/images/auth/otp-view.jpg', import.meta.url).href;
const ratingImg = new URL('../../ui/images/auth/otp-view.jpg', import.meta.url).href;
const selectImg = new URL('../../ui/images/auth/otp-view.jpg', import.meta.url).href;

const getAuthBg = computed((): StyleValue => {
  let url = selectImg;

  switch (route.name) {
    case RouterAuthType.AUTH_EMAIL:
    case RouterAuthType.AUTH_OTP:
      url = otpImg;
    case RouterAuthType.AUTH_REGISTRATION:
      url = registerImg;
    case RouterAuthType.AUTH_RATINGS:
      url = ratingImg;
  }

  return {
    '--bg-image': `url(${url})`,
  };
});

const getConnecting = computed((): SimpleContentDialogType | null => {
  if (connecting.value === LoginMethodType.Google) {
    return {
      loaderIcon: GamIconName.GOOGLE,
      title: localize('auth.connecting.google.title'),
      message: localize('auth.connecting.google.message'),
    };
  } else if (connecting.value === LoginMethodType.Lichess) {
    return {
      loaderIcon: GamIconName.LICHESS,
      title: localize('auth.connecting.lichess.title'),
      message: localize('auth.connecting.lichess.message'),
    };
  }

  return null;
});

onBeforeMount(async () => {
  await authStore.fetchUserInfo(true);
  isAuthLoaded.value = true;

  const { firstName, lastName, birthDate, username, emoji } = userDetails.value || {};

  if (!authStore.isAuthenticated()) {
    return;
  } else if (user.value?.isActive) {
    return router.push({ name: RouterNameType.SPARKS });
  }

  // Disallow onboarding for active users
  // if (rating) {
  //   return router.push('/');
  // }

  // Route to personal details step
  if (!firstName || !lastName || !birthDate || !username || !emoji) {
    return router.push('/auth/registration');
  }

  // Route to ratings step
  // if (!rating) {
  //   return router.push('/auth/ratings');
  // }
});
</script>

<style lang="scss">
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

body.onboarding {
  height: 100% !important;
  min-height: 100svh !important;
}

#gambits-app.onboarding {
  overflow: unset !important;
  overflow-y: auto !important;
  scrollbar-width: none;
  min-height: 100svh !important;
  height: 100% !important;

  &::-webkit-scrollbar {
    display: none;
  }
}

#auth-view {
  width: 100%;
  min-height: 100svh;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;

  #auth-video {
    position: fixed;
    right: 0;
    bottom: 0;
    min-width: 100%;
    min-height: 100%;
    z-index: 1;
  }

  .auth-modal {
    display: flex;
    position: relative;
    // width: 426px;
    // max-height: calc(100vh - var(--spacing-xxl)) !important;
    // padding: var(--spacing-xxl) var(--spacing-menu);
    // padding: var(--spacing-xl) 0;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    gap: var(--spacing-md);
    padding: var(--spacing-l) auto;
    flex-shrink: 0;
    border-radius: var(--radius-large);
    background: radial-gradient(120% 70% at 50% 0%, rgba(255, 255, 255, 0.08) 0%, transparent 55%),
      var(--color-dark-700);
    box-shadow: var(--shadow-md) color-mix(in srgb, var(--color-dark-1000) 33%, transparent);
    z-index: 100;
    width: 100%;
    max-width: 400px;
    margin: var(--spacing-xl) auto var(--spacing-xxl);

    &.is-mobile {
      width: calc(100% - 32px);
      padding: var(--spacing-xxl) var(--spacing-menu) var(--spacing-menu);
      padding: var(--spacing-xl) var(--spacing-md) var(--spacing-menu);
      // padding: var(--spacing-xl) 0;
      padding: 0;
      margin: var(--spacing-xl) auto var(--spacing-xxl);
      min-width: 0;

      .auth-slide-wrapper {
        // margin-bottom: var(--spacing-l);

        .auth-content {
          padding: var(--spacing-xl) calc(var(--spacing-md) * 3) var(--spacing-l);
          gap: var(--spacing-md);
        }

        .auth-methods-group {
          padding: var(--spacing-xl) calc(var(--spacing-md) * 3 + 10px) var(--spacing-xxl);
        }
      }
    }

    .connecting {
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      background: inherit;
      border-radius: inherit;
      z-index: 100;
      top: 0;
    }

    .auth-wrapper {
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      position: relative;

      .auth-content {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;
        gap: var(--spacing-md);
        padding: var(--spacing-xl) calc(var(--spacing-md) * 4) var(--spacing-l);
        padding-bottom: 0;

        .auth-context {
          display: flex;
          width: 100%;
          // padding: var(--spacing-md) 0 var(--spacing-md);
          flex-direction: column;
          justify-content: flex-end;
          align-items: flex-start;
          gap: var(--spacing-l);

          &.left {
            .auth-title,
            .auth-subtitle {
              text-align: left !important;
              text-wrap: balance;
              text-align: center !important;
              line-height: 150%;
            }
          }

          .auth-title {
            font-size: 24px;
            font-style: normal;
            font-weight: 800;
            line-height: 110%; /* 26.4px */
            color: var(--color-white-100);
            // color: var(--color-white-30);
            text-align: center;
            align-self: stretch;
          }

          .auth-subtitle {
            font-size: 18px;
            font-style: normal;
            font-weight: 400;
            line-height: 110%;
            letter-spacing: 0.02em;
            color: var(--color-white-100);
            text-align: center;
            align-self: stretch;

            span {
              color: var(--color-white-100);
            }

            .is-accent {
              color: var(--color-pink);
              font-weight: 800;
            }
          }

          .auth-description {
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 110%; /* 15.4px */
            color: var(--color-white-70);
            opacity: 0.5;

            span {
              color: var(--color-white-100);
            }
          }
        }
      }
    }

    .auth-methods-group {
      display: flex;
      // padding: var(--spacing-l) 10px;
      flex-direction: column;
      justify-content: flex-end;
      align-items: center;
      gap: 12px;
      align-self: stretch;
      padding: var(--spacing-xxl) calc(var(--spacing-md) * 4 + 10px) var(--spacing-xxl);

      .methods-row {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 12px;
        align-self: stretch;
      }
    }

    .back-button,
    .close-button,
    .logout-button,
    .continue-button {
      position: absolute;
      bottom: -16px;
    }
  }

  &:before,
  &:after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }

  &:before {
    background: linear-gradient(81deg, var(--color-pink-30) 0%, var(--color-dark-500) 100%);
    filter: brightness(0.66);
    z-index: 3;
  }

  &:after {
    background: var(--bg-image);
    background-size: cover;
    background-position: center center;
    filter: saturate(0);
    z-index: 4;
    opacity: 0.5;
    mix-blend-mode: lighten;
  }

  &.is-landing {
    &:before,
    &:after {
      position: fixed;
    }
  }

  .auth-context.policy {
    color: var(--color-white-30);
    text-align: center;
    text-wrap: balance;

    span {
      font-size: 13px;
      line-height: 20px;
    }

    span span {
      color: var(--color-white-100);
      opacity: 0.5;
    }

    a,
    a:link,
    a:visited {
      color: var(--color-white-100);
      opacity: 0.5;
      text-decoration: none;
    }
    @include hover-capable {
      a:hover {
        color: var(--color-white-70);
        opacity: 1;
      }
    }
  }
}

/* ── Landing hero (first viewport page) ────── */

// Scoped under #auth-view so these showcase styles cannot leak onto other
// views that happen to reuse the same class names (board, walkfield, etc.).
#auth-view {
  /* Over the next section, whose city starts on this screen: the city shows through under the sign-in. */
  .auth-landing-hero {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 103;
    min-height: 100svh;

    &.is-landing {
      flex-direction: column;
    }
  }
}

/* ── Showcase section ──────────────────────── */

#auth-view {
  .auth-park {
    position: relative;
    z-index: 101;
    width: 100%;
    clip-path: inset(0);
  }

  /*
   * Fixed and clipped to the section: the walk and the tree scroll over a still park.
   * The photo is drawn 70% of the way down so its trees stay in view on wide screens too.
   */
  .auth-park-photo {
    --photo-y: 0.7;
    position: fixed;
    inset: 0;
    background:
      var(--park-image) 50% calc(var(--photo-y) * 100%) / cover no-repeat,
      var(--color-dark-700);
    container-type: size;
    pointer-events: none;
  }

  .auth-showcase {
    width: 100%;
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;

    padding: 0 0 var(--spacing-xxl);
    overflow: hidden;

    /*
     * The park stays out of focus behind Solve, the walk, and Start.
     * Move unseen is the next section, so the photo is sharp again there.
     */
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: -1;
      -webkit-backdrop-filter: blur(10px);
      backdrop-filter: blur(10px);
      pointer-events: none;
    }
  }

  .showcase-section {
    position: relative;
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-xl);
    width: 100%;
    max-width: 720px;
    padding: 0 var(--spacing-l);
  }

  .showcase-hero {
    position: relative;
    z-index: 101;
    width: 100%;
    min-height: 200px;
    background: var(--color-dark-700);
  }

  .showcase-carousel-track {
    display: flex;
    width: 100%;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .showcase-carousel-slide {
    flex: 0 0 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    padding: calc(var(--spacing-xxl) + 28px) var(--spacing-l) calc(var(--spacing-xxl) + 20px);
    scroll-snap-align: center;
    scroll-snap-stop: always;
    box-sizing: border-box;
  }

  .showcase-carousel-arrow {
    display: none;
    position: absolute;
    top: 50%;
    z-index: 2;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    background: none;
    color: var(--color-white-60);
    font-size: 36px;
    font-weight: 300;
    line-height: 1;
    cursor: pointer;
    transform: translateY(-50%);

    &.is-prev {
      left: var(--spacing-l);
    }

    &.is-next {
      right: var(--spacing-l);
    }

    &.disabled {
      opacity: 0.25;
      cursor: default;
    }
  }

  .showcase-carousel-dots {
    position: absolute;
    left: 0;
    right: 0;
    bottom: var(--spacing-l);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    pointer-events: none;

    button {
      pointer-events: auto;
    }
  }

  .showcase-carousel-dot {
    width: 6px;
    height: 6px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: var(--color-white-20);
    cursor: pointer;
    transition:
      background 150ms ease,
      width 150ms ease,
      border-radius 150ms ease;

    &.active {
      width: 14px;
      border-radius: 3px;
      background: var(--color-white-70);
    }
  }

  .showcase-mode-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--color-white-40);
  }

  .showcase-hero > .showcase-mode-label {
    position: absolute;
    top: var(--spacing-xxl);
    left: 0;
    right: 0;
    z-index: 2;
    text-align: center;
    pointer-events: none;
  }

  .showcase-headline {
    position: absolute;
    top: calc(var(--spacing-xxl) + 28px);
    left: var(--spacing-l);
    right: var(--spacing-l);
    z-index: 2;
    margin: 0 auto;
    max-width: 560px;
    font-size: 26px;
    font-weight: 800;
    line-height: 140%;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    text-align: center;
    color: var(--color-pink);
    pointer-events: none;
  }

  .showcase-hero.has-headline .showcase-carousel-slide {
    padding-top: calc(var(--spacing-xxl) + 96px);
  }

  .showcase-carousel-slide.is-accent .showcase-tagline {
    color: var(--color-pink);
  }

  .showcase-tagline .is-accent-word {
    color: var(--color-pink);
  }

  .showcase-sport-hero {
    position: relative;
    z-index: 11;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: var(--spacing-xxl) var(--spacing-l) var(--spacing-xl);
    gap: 28px;

    .showcase-mode-label {
      text-align: center;
      max-width: 560px;
      line-height: 140%;
    }
  }

  /* The collection's glass, above Unpaired. The glint and the crown take the accent color. */
  .showcase-crown {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    margin-bottom: -8px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;

    @include press;
  }

  .showcase-sphere {
    --sphere-size: 56px;
    pointer-events: none;

    .circle {
      background: radial-gradient(
          circle at var(--light-x) var(--light-y),
          color-mix(in oklch, var(--color-pink) 22%, transparent) 0%,
          rgba(255, 255, 255, 0.08) 42%,
          transparent 72%
        ),
        transparent;
      border-color: rgba(255, 255, 255, 0.28);
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
    }

    .circle::before {
      width: 26%;
      height: 14%;
      background: radial-gradient(
        ellipse at center,
        color-mix(in oklch, var(--color-pink) 45%, white) 0%,
        transparent 70%
      );
    }

    .circle::after {
      display: none;
    }

    .icon-3d-wrapper {
      width: 46%;
      height: 46%;
      color: var(--color-pink);
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
    }
  }

  .showcase-title-stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .showcase-sport-title {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.35em;
    margin: 0;
    max-width: 560px;
    font-size: 26px;
    font-weight: 800;
    line-height: 140%;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    text-align: center;
    color: var(--color-white);

    .is-accent {
      color: var(--color-pink);
    }

    &.is-multiline {
      flex-direction: column;
      align-items: center;
      gap: 0;
    }
  }

  .showcase-board-stage {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: var(--spacing-l);
  }

  .showcase-title-line {
    display: flex;
    gap: 0.35em;
  }

  /* A part under a section's title: its name, then the thing itself. */
  .showcase-part-title {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.4em;
    margin: 0;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--color-white);

    .is-accent {
      color: var(--color-pink);
    }
  }

  .showcase-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-l);
    box-sizing: border-box;
    width: 100%;
    padding: var(--spacing-l) var(--spacing-l) var(--spacing-xxl);
  }

  .shuffle-pair-button {
    display: block;
    width: min(100%, 420px);
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    cursor: pointer;
  }

  .showcase-step-line {
    max-width: 320px;
    margin: 0;
    font-size: 15px;
    line-height: 1.45;
    text-align: center;
    color: var(--color-white-70);
  }

  /* As wide as the sheet above it, the toolbar at its left end and the photo at its right, as in the app. */
  .showcase-tools {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-md);
    width: min(100%, 420px);
  }

  /* The recommendation sheet's toolbar: one dark surface under the row, its discs on the lighter fill. */
  .showcase-toolbar {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    padding: var(--spacing-s);
    border-radius: 999px;
    background: var(--color-dark-900);
    box-shadow:
      inset 0 0 0 1px var(--color-white-10),
      0 8px 24px var(--color-dark-50);
  }

  .toolbar-disc,
  .toolbar-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    border: 0;
    background: var(--color-dark-500);
    color: var(--color-white-70);
  }

  .toolbar-disc {
    width: 44px;
    padding: 0;
    border-radius: 50%;
    cursor: pointer;
    transition: background var(--transition);

    @include hover {
      background: linear-gradient(var(--color-white-8), var(--color-white-8)), var(--color-dark-500);
    }

    @include press;
  }

  .toolbar-colour {
    width: 16px;
    height: 16px;
    border-radius: 50%;

    &.is-white,
    &.is-black {
      border: 1px solid var(--color-white-30);
    }

    &.is-white {
      background: var(--color-white);
    }

    &.is-black {
      background: #000;
    }
  }

  .toolbar-chip {
    gap: var(--spacing-s);
    padding: 0 var(--spacing-l);
    border-radius: 44px;
    color: var(--color-white);
    font-size: 15px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  /* The photo a game leaves behind, round like the toolbar's discs so it fills the pill, with a white edge. */
  .toolbar-photo {
    flex: none;
    width: 44px;
    height: 44px;
    padding: 0;
    overflow: hidden;
    border: 1px solid var(--color-white-100);
    border-radius: 50%;
    background: transparent;
    cursor: pointer;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      pointer-events: none;
      user-select: none;
    }

    @include press;
  }

  .auth-social {
    width: 100%;
    position: relative;
    z-index: 102;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
    overflow: visible;
    background: transparent;

    /* The section's title stands on its own, clear of the sign-in above and the first step below. */
    .auth-city > .showcase-sport-hero {
      padding-top: var(--spacing-xxl);
      padding-bottom: var(--spacing-xl);

      .showcase-title-stack {
        gap: var(--spacing-md);
      }
    }
  }

  /* A band of the city behind the title: the title in its middle, a spot in each far corner. */
  .auth-city {
    position: relative;
    width: 100%;
    min-height: clamp(520px, 80svh, 720px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  /*
   * Up into the first screen to just under the sign-in and down into the first step's card, faded in and out by the same stretch.
   * The fade is eased at both of its ends: a straight one leaves a visible line where it turns solid.
   */
  .auth-city-map {
    --city-fade: 320px;
    --city-mask: linear-gradient(
      to bottom,
      rgb(0 0 0 / 0) 0,
      rgb(0 0 0 / 0.04) calc(var(--city-fade) * 0.125),
      rgb(0 0 0 / 0.15) calc(var(--city-fade) * 0.25),
      rgb(0 0 0 / 0.32) calc(var(--city-fade) * 0.375),
      rgb(0 0 0 / 0.5) calc(var(--city-fade) * 0.5),
      rgb(0 0 0 / 0.68) calc(var(--city-fade) * 0.625),
      rgb(0 0 0 / 0.85) calc(var(--city-fade) * 0.75),
      rgb(0 0 0 / 0.96) calc(var(--city-fade) * 0.875),
      #000 var(--city-fade),
      #000 calc(100% - var(--city-fade)),
      rgb(0 0 0 / 0.96) calc(100% - var(--city-fade) * 0.875),
      rgb(0 0 0 / 0.85) calc(100% - var(--city-fade) * 0.75),
      rgb(0 0 0 / 0.68) calc(100% - var(--city-fade) * 0.625),
      rgb(0 0 0 / 0.5) calc(100% - var(--city-fade) * 0.5),
      rgb(0 0 0 / 0.32) calc(100% - var(--city-fade) * 0.375),
      rgb(0 0 0 / 0.15) calc(100% - var(--city-fade) * 0.25),
      rgb(0 0 0 / 0.04) calc(100% - var(--city-fade) * 0.125),
      rgb(0 0 0 / 0) 100%
    );
    position: absolute;
    inset: -280px 0 -200px;
    z-index: -1;
    background: var(--city-image) center / cover no-repeat;
    -webkit-mask-image: var(--city-mask);
    mask-image: var(--city-mask);
    pointer-events: none;
  }

  /* Over the title's box, which spans the band, so the pins take their own taps. */
  .auth-city-pins {
    position: absolute;
    inset: 0;
    z-index: 12;
    pointer-events: none;
  }

  /* Corners held in from the edge, so a pin and its name stay on screen from a phone to a desktop. */
  .city-spot {
    --city-spot-inset: clamp(72px, 20%, 280px);
    position: absolute;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    color: inherit;
    opacity: 0;
    transform: translate(-50%, -50%);
    translate: 0 -24px;
    transition:
      opacity 300ms ease,
      translate 400ms cubic-bezier(0.2, 0.8, 0.2, 1);

    @include press($transform: translate(-50%, -50%));

    &.is-top {
      left: calc(100% - var(--city-spot-inset));
      top: 22%;
    }

    &.is-bottom {
      left: var(--city-spot-inset);
      top: 76%;
      transition-delay: 150ms;
    }
  }

  /* The pins drop onto the city, the second just after the first. */
  .auth-city.is-pin-shown .city-spot {
    opacity: 1;
    translate: 0 0;
    pointer-events: auto;

    @include hover {
      opacity: 0.85;
    }
  }

  /* The two ways to a handshake, read as one pair of lines between the sheets. */
  .showcase-step-between {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-s);
  }

  .showcase-step-or {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--color-white-50);
  }

  /* The page's last word, on the dark the book is set on, then the pin. */
  /* Over the park, so the pin's instructions can open up across the photo. */
  .auth-footer {
    position: relative;
    z-index: 102;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-xl);
    width: 100%;
    box-sizing: border-box;
    padding: var(--spacing-xxl) var(--spacing-l) calc(var(--spacing-xxl) + env(safe-area-inset-bottom));
    background: var(--color-dark-700);
  }

  .auth-footer-risk {
    max-width: 280px;
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.45;
    text-align: center;
    color: var(--color-white-70);

    .is-accent {
      color: var(--color-pink);
    }
  }

  .auth-footer-pin {
    display: flex;
    align-items: center;
    gap: var(--spacing-l);
  }

  .auth-footer-line {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--color-white-70);
  }

  .auth-book {
    position: relative;
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0 var(--spacing-l) calc(var(--spacing-xxl) * 2.5);
    background: var(--color-dark-700);
  }

  /* Lit from the top like the auth modal, so My 960 reads apart from the photos around it. */
  .auth-collection {
    position: relative;
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    padding: 0 var(--spacing-l) var(--spacing-xxl);
    background: radial-gradient(120% 70% at 50% 0%, rgba(255, 255, 255, 0.08) 0%, transparent 55%),
      var(--color-dark-700);

    .showcase-title-stack {
      gap: var(--spacing-md);
    }
  }

  /* A screen of open park under the title, so the pinned tree stands clear at the end of the page. */
  .auth-tree {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100svh;
    padding: 0 var(--spacing-l);
    pointer-events: none;
    text-shadow: 0 1px 8px rgba(0, 0, 0, 0.45);
  }

  /*
   * The spot on the photo's big tree. Its place is the canopy's pixel in the photo, scaled and
   * offset the way cover and the photo's position place the photo in the layer.
   */
  .tree-spot {
    --tree-circle: 80px;
    --tree-ring-offset: 6px;
    --photo-w: 1350;
    --photo-h: 1800;
    --spot-x: 844;
    --spot-y: 984;
    --photo-scale: max(100cqw / var(--photo-w), 100cqh / var(--photo-h));
    position: absolute;
    left: calc((100cqw - var(--photo-w) * var(--photo-scale)) / 2 + var(--spot-x) * var(--photo-scale));
    top: calc((100cqh - var(--photo-h) * var(--photo-scale)) * var(--photo-y) + var(--spot-y) * var(--photo-scale));
    display: flex;
    flex-direction: column;
    align-items: center;
    width: calc(var(--tree-circle) + 2 * var(--tree-ring-offset));
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    transform: translate(-50%, -50%);
    pointer-events: none;

    @include press($transform: translate(-50%, -50%));

    &.is-sphere,
    &.is-pin {
      pointer-events: auto;
    }
  }

  /* A solid white ring outside the glass. A border on the glass itself washes out to gray. */
  .tree-spot-rim {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 1;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    pointer-events: none;
    background: #fff;
    opacity: 0;
    translate: -50% -50%;
    mask: radial-gradient(circle, transparent 24px, #000 24.5px);
  }

  .tree-spot.is-sphere .tree-spot-rim {
    opacity: 1;
  }

  .tree-spot.is-pin .tree-spot-rim {
    opacity: 0;
  }

  /* The profile's glass, with a white crown, on the tree before the pin. */
  .tree-spot-mark {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: calc(var(--tree-circle) + 2 * var(--tree-ring-offset));
    height: calc(var(--tree-circle) + 2 * var(--tree-ring-offset));
  }

  .tree-spot-sphere {
    --sphere-size: 48px;
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 2;
    color: var(--color-white);
    opacity: 0;
    translate: -50% calc(-50% - 24px);
    pointer-events: none;
    transition:
      opacity 300ms ease,
      translate 400ms cubic-bezier(0.2, 0.8, 0.2, 1);

    /* A thick Apple glass. The button's drop-shadow used to sit above this and keep the photo sharp. */
    :deep(.circle) {
      background: radial-gradient(
          circle at var(--light-x) var(--light-y),
          rgba(255, 255, 255, 0.95) 0%,
          rgba(255, 255, 255, 0.28) 22%,
          transparent 46%
        ),
        radial-gradient(circle at var(--shadow-x) var(--shadow-y), rgba(0, 0, 0, 0.22) 0%, transparent 52%),
        rgba(255, 255, 255, 0.62);
      border: 0;
      backdrop-filter: blur(64px) saturate(1.8);
      -webkit-backdrop-filter: blur(64px) saturate(1.8);
      box-shadow:
        inset 0 1px 0 #fff,
        0 10px 24px rgba(0, 0, 0, 0.28);
    }

    :deep(.circle::before) {
      width: 48%;
      height: 30%;
      background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0) 68%);
    }

    :deep(.icon-3d-wrapper) {
      color: var(--color-white);
      filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.45));
    }
  }

  .tree-spot.is-sphere .tree-spot-sphere {
    opacity: 1;
    translate: -50% -50%;
  }

  .tree-spot.is-pin .tree-spot-sphere {
    opacity: 0;
  }

  .tree-spot-pin {
    display: block;
    opacity: 0;
    filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.45));
    translate: 0 -24px;
    transition:
      opacity 300ms ease,
      translate 400ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  /* The pin drops onto the tree after the crown has been seen. */
  .tree-spot.is-pin .tree-spot-pin {
    opacity: 1;
    translate: 0 0;
  }

  .tree-spot-board {
    position: absolute;
    top: -36px;
    left: 50%;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    transform: translateX(-50%);
  }

  .tree-spot-board-icon {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    overflow: hidden;
    background-repeat: no-repeat;
    background-size: 50% 50%;
    background-position:
      0 0,
      100% 0,
      0 100%,
      100% 100%;
    background-image: linear-gradient(#f0d9b5, #f0d9b5), linear-gradient(#b58863, #b58863),
      linear-gradient(#b58863, #b58863), linear-gradient(#f0d9b5, #f0d9b5);
  }

  .tree-spot-ring {
    display: block;
    position: relative;
    width: calc(var(--tree-circle) + 2 * var(--tree-ring-offset));
    height: calc(var(--tree-circle) + 2 * var(--tree-ring-offset));
    padding: var(--tree-ring-offset);
    border-radius: 50%;
    box-sizing: border-box;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 50%;
      padding: 2px;
      background: rgba(255, 255, 255, 0.7);
      mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
  }

  .tree-spot-main {
    display: block;
    width: var(--tree-circle);
    height: var(--tree-circle);
    overflow: hidden;
    border-radius: 50%;
    background: var(--color-dark-600);

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .showcase-tagline,
  .showcase-title {
    font-size: 26px;
    font-weight: 800;
    line-height: 140%;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-white);
    text-align: center;
    margin: 0;
    max-width: 380px;
  }

  .showcase-carousel-slide .showcase-tagline {
    min-height: 4.2em;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 48px;

    span {
      max-width: 380px;
      white-space: pre-line;
    }
  }

  @media (min-width: 976px) {
    .showcase-carousel-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .board-status-row {
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    /* White, unlike the walk screen: it sits on the park photo, where grey washes out on the sky. */
    color: var(--color-white);
    letter-spacing: 0.02em;

    .status-field {
      color: var(--color-pink);
    }
  }

  /* ── Walkboard ─────────────────────────────── */

  .board-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .board-area {
    position: relative;
    width: 100%;
    max-width: 380px;
    aspect-ratio: 1;
    border-radius: calc(8px + 5px);
    box-shadow:
      0 0 0 0.5px rgba(255, 255, 255, 0.08),
      0 0 40px 8px oklch(0.78 0.24 var(--hue) / 0.05);

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 5px;
      background: rgba(255, 255, 255, 0.35);
      mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      mask-composite: exclude;
      -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      pointer-events: none;
    }
  }

  .board-inner {
    position: absolute;
    inset: 5px;
    border-radius: 8px;
    overflow: hidden;
  }

  .walkfield-grid {
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-columns: repeat(var(--zoom-n, 8), 1fr);
    grid-template-rows: repeat(var(--zoom-n, 8), 1fr);
    gap: 1px;
    border-radius: 8px;
    overflow: hidden;
  }

  .walkfield-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    background: oklch(0 0 var(--hue));
    cursor: pointer;
    position: relative;
    transition:
      background 150ms ease,
      box-shadow 150ms ease;
    user-select: none;
    -webkit-tap-highlight-color: transparent;

    @include hover {
      filter: brightness(1.2);
    }

    &.selected {
      background: var(--color-pink-30);
      animation: cell-pulse 2.5s ease-in-out infinite;
      box-shadow: none;
    }
  }

  .walkfield-selected-label {
    font-size: 12px;
    font-weight: 700;
    color: #ffffff;
    pointer-events: none;
  }

  .walkfield-row-label,
  .walkfield-col-label {
    font-size: 6px;
    font-weight: 600;
    color: var(--color-white-30);
    pointer-events: none;
  }

  .walkfield-zoom-label {
    font-size: calc(72px / var(--zoom-n));
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.02em;
    pointer-events: none;
  }
}

@keyframes cell-pulse {
  0%,
  100% {
    opacity: 0.85;
  }
  50% {
    opacity: 1;
  }
}

@keyframes d5-pulse {
  0%,
  100% {
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.15);
  }

  50% {
    box-shadow:
      0 0 18px rgba(255, 255, 255, 0.4),
      0 0 36px var(--color-pink-20);
  }
}

/* ── Walk path (full-width background) ───── */

#auth-view {
  .walk-path-bg {
    position: absolute;
    inset: 0;
    z-index: 10;
    pointer-events: none;
  }

  .walk-path-svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 3;
    pointer-events: none;
  }

  .walk-streak-line {
  }

  .walk-sq {
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 2px;
    transform: translate(-50%, -50%);
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    &.walk-sq-correct {
      background: var(--color-correct);
    }

    &.walk-sq-incorrect {
      background: var(--color-incorrect);
    }

    &.walk-sq-corrected {
      background: var(--color-incorrect);
    }
  }

  .walk-corrected-dot {
    width: 40%;
    height: 40%;
    border-radius: 50%;
    background: var(--color-correct);
  }

  .walk-spot {
    position: absolute;
    transform: translate(-50%, -50%);
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border: none;
    background: none;
  }

  .walk-spot-empty {
    width: 14px;
    height: 14px;
    border-radius: 2px;
    border: 2px solid var(--color-white);
  }

  .walk-spot-full {
    width: 14px;
    height: 14px;
    border-radius: 2px;
    background: var(--color-white);
  }

  /* ── Start CTA (walk style) ──────────────── */

  .start-cta-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .zoom-glyph {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #ffffff;
    touch-action: manipulation;
    transition: opacity 150ms ease;

    &.disabled,
    &:disabled {
      opacity: 0.3;
      pointer-events: none;
    }

    &:active:not(:disabled) {
      transform: scale(0.92);
    }
  }

  .zoom-glyph-icon {
    display: block;
    font-family: 'Hanken Grotesk', sans-serif;
    font-weight: 900;
    font-size: 56px;
    line-height: 1;
    opacity: 0.35;
    user-select: none;
    -webkit-text-stroke: 1.7px currentColor;
    /* +/− are drawn on the font's math axis, which sits below the line-box
       centre, so box centring alone leaves them low against the Start label. */
    transform: translateY(-0.06em);
  }

  .start-cta {
    width: 76px;
    height: 76px;
    border-radius: 50%;
    border: none;
    padding: 0;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all 200ms ease;
    background: linear-gradient(var(--color-pink-30), var(--color-pink-30)), var(--color-dark-700);
    color: var(--color-white);

    &:active {
      transform: scale(0.95);
    }
  }
}
</style>
